import dayjs from 'dayjs'
import { DateWindow, IsoDate, calendarMonthBefore, shiftIsoDate } from '../math/windows'
import { TimeRangePreset, TimeRangePresetKey } from '../../reporting_v2/constants/time-range-options'

export type OrgDashboardRangeKey =
    | 'yesterday'
    | 'last_7_days'
    | 'last_14_days'
    | 'last_30_days'
    | 'last_90_days'
    | 'last_month'

export const ORG_DASHBOARD_RANGE_KEYS: OrgDashboardRangeKey[] = [
    'yesterday',
    'last_7_days',
    'last_14_days',
    'last_30_days',
    'last_90_days',
    'last_month',
]

const DAYS_IN_TRAILING_RANGE: Record<Exclude<OrgDashboardRangeKey, 'last_month'>, number> = {
    yesterday: 1,
    last_7_days: 7,
    last_14_days: 14,
    last_30_days: 30,
    last_90_days: 90,
}

/** Every range ends on the latest complete date, so a range never includes a day that is still accruing data. */
export const resolveRangeKey = (key: OrgDashboardRangeKey, latestCompleteDate: IsoDate): DateWindow => {
    if (key === 'last_month') {
        // The latest complete date is yesterday, so the day after it is today: Last Month is the month before today's.
        return calendarMonthBefore(shiftIsoDate(latestCompleteDate, 1))
    }
    return { since: shiftIsoDate(latestCompleteDate, -(DAYS_IN_TRAILING_RANGE[key] - 1)), through: latestCompleteDate }
}

export const ORG_DASHBOARD_PRESET_LABELS: Record<OrgDashboardRangeKey, string> = {
    yesterday: 'Yesterday',
    last_7_days: 'Last 7 Days',
    last_14_days: 'Last 14 Days',
    last_30_days: 'Last 30 Days',
    last_90_days: 'Last 90 Days',
    last_month: 'Last Month',
}

/** Presets for the organization dashboard: complete days only, each ending on the latest complete date. */
export const buildOrgDashboardTimeRangePresets = (
    latestCompleteDate: IsoDate,
): Array<TimeRangePreset & { rangeKey: OrgDashboardRangeKey }> =>
    ORG_DASHBOARD_RANGE_KEYS.map((rangeKey) => ({
        label: ORG_DASHBOARD_PRESET_LABELS[rangeKey],
        value: () => {
            const dateWindow = resolveRangeKey(rangeKey, latestCompleteDate)
            return [dayjs(dateWindow.since), dayjs(dateWindow.through)]
        },
        key: TimeRangePresetKey.CUSTOM,
        rangeKey,
    }))
